import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, RotateCcw } from 'lucide-react';
import type { Mailbox } from '../shared/api/types';
import { Button } from '../shared/ui/Button';
import { Skeleton } from '../shared/ui/Skeleton';
import { ConfirmModal } from '../shared/ui/ConfirmModal';
import { toast } from '../shared/ui/toast';
import { useAuth } from '../auth/useAuth';
import {
  useActivateMailbox,
  useDeleteMailbox,
  useDeletedMailboxes,
  useMailboxes,
  usePinMailbox,
  useQuota,
  useUnpinMailbox,
  useUpdateMailbox,
} from '../features/mailbox/api';
import { MailboxCard } from '../features/mailbox/components/MailboxCard';
import { CreateMailboxModal } from '../features/mailbox/components/CreateMailboxModal';
import { RestoreMailboxesModal } from '../features/mailbox/components/RestoreMailboxesModal';
import { BulkCleanupModal } from '../features/mailbox/components/BulkCleanupModal';
import { QuotaHeaderCard } from '../features/mailbox/components/QuotaHeaderCard';
import { QuotaPausedModal } from '../features/mailbox/components/QuotaPausedModal';
import { useRetention } from '../features/mailbox/useRetention';
import { useFirstMailboxBootstrap } from '../features/mailbox/useFirstMailbox';
import { useOnboardingAnchor } from '../features/onboarding/useOnboardingAnchor';

export function MailboxesPage() {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading: authLoading, login } = useAuth();

  const mailboxesQuery = useMailboxes();
  const quotaQuery = useQuota();
  const deletedQuery = useDeletedMailboxes(isAuthenticated);
  const retention = useRetention();

  const pin = usePinMailbox();
  const unpin = useUnpinMailbox();
  const del = useDeleteMailbox();
  const update = useUpdateMailbox();
  const activate = useActivateMailbox();

  const [createOpen, setCreateOpen] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [pausedOpen, setPausedOpen] = useState(false);
  const [toDelete, setToDelete] = useState<Mailbox | null>(null);

  // Первый вход в услугу: ящиков нет и корзина пуста — бэкенд заводит первый ящик сам.
  const listsLoaded = mailboxesQuery.isSuccess && deletedQuery.isSuccess;
  const neverHadMailboxes =
    (mailboxesQuery.data?.items.length ?? 0) === 0 && (deletedQuery.data?.items.length ?? 0) === 0;
  const creatingFirst = useFirstMailboxBootstrap(isAuthenticated, listsLoaded, neverHadMailboxes);

  if (!authLoading && !isAuthenticated) {
    return (
      <div className="mx-auto max-w-md rounded-2xl bg-surface p-8 text-center shadow-card">
        <p className="text-fg-muted">{t('auth.loginPrompt')}</p>
        <Button
          className="mt-4"
          onClick={() => {
            login();
          }}
        >
          {t('auth.login')}
        </Button>
      </div>
    );
  }

  const data = mailboxesQuery.data;
  const all = data?.items ?? [];
  const pinned = all.filter((m) => m.pinned);
  const rest = all.filter((m) => !m.pinned);
  const hasDeleted = (deletedQuery.data?.items.length ?? 0) > 0;
  const pinLimit = quotaQuery.data?.pinned.limit ?? 3;

  const onPin = (m: Mailbox) => {
    if (pinned.length >= pinLimit) {
      toast.warning(t('mailbox.toasts.pinLimitTitle'), t('mailbox.toasts.pinLimitBody'));
      return;
    }
    pin.mutate(m.id, { onSuccess: () => toast.success(t('mailbox.toasts.pinned')) });
  };
  const onUnpin = (m: Mailbox) => {
    unpin.mutate(m.id, { onSuccess: () => toast.success(t('mailbox.toasts.unpinned')) });
  };
  const onToggleForwarding = (m: Mailbox) => {
    update.mutate(
      { id: m.id, forwardingEnabled: !m.forwardingEnabled },
      {
        onSuccess: () =>
          toast.success(
            m.forwardingEnabled ? t('mailbox.toasts.forwardingOff') : t('mailbox.toasts.forwardingOn')
          ),
      }
    );
  };
  const onConfirmDelete = () => {
    if (!toDelete) return;
    del.mutate(toDelete.id, {
      onSuccess: () => {
        toast.success(t('mailbox.toasts.deleted'));
        setToDelete(null);
      },
    });
  };

  const onActivate = (m: Mailbox) => {
    activate.mutate(m.id, { onSuccess: () => toast.success(t('mailbox.toasts.activated')) });
  };

  const actions = { onPin, onUnpin, onActivate, onToggleForwarding, onDelete: setToDelete };
  const isEmpty = !mailboxesQuery.isLoading && all.length === 0;

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-3xl font-bold text-fg">{t('mailbox.pageTitle')}</h1>
        <p className="mt-1 max-w-2xl text-sm text-fg-muted">
          {quotaQuery.data
            ? t('app.subtitleCount', { count: quotaQuery.data.active.limit })
            : t('app.subtitle')}
        </p>
      </header>

      {mailboxesQuery.isLoading || creatingFirst ? (
        <Skeleton className="h-28 w-full rounded-2xl" />
      ) : isEmpty ? (
        <EmptyState
          hasDeleted={hasDeleted}
          onAdd={() => {
            setCreateOpen(true);
          }}
          onRestore={() => {
            setRestoreOpen(true);
          }}
        />
      ) : (
        <>
          <QuotaHeaderCard
            mailboxes={all}
            quota={quotaQuery.data}
            onAdd={() => {
              setCreateOpen(true);
            }}
            onCleanup={() => {
              setCleanupOpen(true);
            }}
            onShowPaused={() => {
              setPausedOpen(true);
            }}
          />

          {pinned.length > 0 && (
            <section className="space-y-2">
              <h2 className="px-1 font-semibold text-fg">{t('mailbox.sectionPinned')}</h2>
              {/* Якорь онбординга ставит первая карточка в порядке отображения. */}
              {pinned.map((m, i) => (
                <MailboxCard key={m.id} mailbox={m} actions={actions} anchored={i === 0} />
              ))}
            </section>
          )}

          <section className="space-y-2">
            <div className="flex items-center justify-between px-1">
              <h2 className="font-semibold text-fg">{t('mailbox.sectionAll')}</h2>
              {hasDeleted && (
                <button
                  type="button"
                  onClick={() => {
                    setRestoreOpen(true);
                  }}
                  className="flex items-center gap-1.5 text-sm font-medium text-brand"
                >
                  <RotateCcw className="h-4 w-4" /> {t('mailbox.restoreDeleted')}
                </button>
              )}
            </div>
            {rest.map((m, i) => (
              <MailboxCard
                key={m.id}
                mailbox={m}
                actions={actions}
                anchored={pinned.length === 0 && i === 0}
              />
            ))}
          </section>
        </>
      )}

      <CreateMailboxModal open={createOpen} onOpenChange={setCreateOpen} />
      <RestoreMailboxesModal open={restoreOpen} onOpenChange={setRestoreOpen} />
      <BulkCleanupModal open={cleanupOpen} onOpenChange={setCleanupOpen} />
      <QuotaPausedModal open={pausedOpen} onOpenChange={setPausedOpen} quota={quotaQuery.data} />
      <ConfirmModal
        open={toDelete !== null}
        onOpenChange={(v) => {
          if (!v) setToDelete(null);
        }}
        title={t('mailbox.delete.title', { address: toDelete?.address.full ?? '' })}
        description={t('mailbox.delete.subtitle', { count: retention.mailboxTrashDays })}
        confirmLabel={t('mailbox.delete.confirm')}
        onConfirm={onConfirmDelete}
        loading={del.isPending}
      />
    </div>
  );
}

function EmptyState({
  hasDeleted,
  onAdd,
  onRestore,
}: {
  hasDeleted: boolean;
  onAdd: () => void;
  onRestore: () => void;
}) {
  const { t } = useTranslation();
  // Без ящиков карточка квоты не рендерится, поэтому шаг «создайте первый адрес»
  // указывает на кнопку пустого состояния — иначе он деградировал бы до центра экрана.
  const addButtonAnchor = useOnboardingAnchor('mailbox.addButton');

  return (
    <div className="rounded-2xl bg-surface px-6 py-16 text-center shadow-card">
      <h2 className="text-2xl font-bold text-fg">{t('mailbox.emptyTitle')}</h2>
      <p className="mt-2 text-fg-muted">
        {hasDeleted ? t('mailbox.emptySubtitleRestore') : t('mailbox.emptySubtitle')}
      </p>
      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-center">
        <Button className="w-full sm:w-auto" onClick={onAdd} {...addButtonAnchor}>
          <Plus className="h-4 w-4" /> {t('mailbox.addMailbox').replace('+ ', '')}
        </Button>
        {hasDeleted && (
          <Button className="w-full sm:w-auto" variant="secondary" onClick={onRestore}>
            {t('mailbox.emptyRestore').replace('↺ ', '')}
          </Button>
        )}
      </div>
    </div>
  );
}
